import { BLOCKS } from "./blocks.js";
import * as THREE from "three";
import { animalModel } from "./craft-models.js";
import { disposeObj } from "./entities.js";
import { groundHeight, homeRegion, REGION } from "./discovery-terrain.js";
import { hash2 } from "./util.js";
import { addItem, amount, STACK_MAX, selectedItem } from "./crafting.js";
import { audio } from "./audio.js";
const TYPES = {
  sheep: { hp: 3, drops: { wool: 3, food: 1 } },
  cow: { hp: 5, drops: { hide: 3, food: 2 } },
  boar: { hp: 7, drops: { hide: 2, food: 3 } },
};
export class Wildlife {
  constructor(E) {
    this.E = E;
    this.animals = new Map();
  }
  addRegion(rx, rz) {
    const home = homeRegion(rx, rz);
    if (home && !(rx === 0 && rz === -1)) return;
    const seeds = home
      ? [
          [10, -9, "sheep"],
          [20, -20, "cow"],
          [24, -8, "boar"],
        ]
      : [
          [
            rx * REGION + 10,
            rz * REGION + 7,
            ["sheep", "cow", "boar"][Math.floor(hash2(rx, rz, 63) * 3) % 3],
          ],
        ];
    seeds.forEach(([x, z, kind], i) => {
      const id = `${rx},${rz}:${i}`;
      if (this.animals.has(id)) return;
      const model = animalModel(kind),
        obj = model.root;
      obj.position.set(x + 0.5, groundHeight(x, z) + 1, z + 0.5);
      this.E.group.add(obj);
      const a = {
        id,
        rx,
        rz,
        kind,
        obj,
        model,
        hp: TYPES[kind].hp,
        home: obj.position.clone(),
        angle: hash2(rx + i, rz, 9) * Math.PI * 2,
        time: i * 2,
        flash: 0,
      };
      obj.userData.animal = a;
      this.animals.set(id, a);
    });
  }
  removeRegion(rx, rz) {
    for (const [id, a] of this.animals)
      if (a.rx === rx && a.rz === rz) {
        this.E.group.remove(a.obj);
        disposeObj(a.obj);
        this.animals.delete(id);
      }
  }
  target(ray) {
    const hits = ray.intersectObjects(
      [...this.animals.values()].filter((a) => a.obj.visible).map((a) => a.obj),
      true,
    );
    const o = ray.ray.origin,
      d = ray.ray.direction;
    const wall = this.E.world.raycast(o.x, o.y, o.z, d.x, d.y, d.z, 30, (id) =>
      this.E.world.solidBlock ? this.E.world.solidBlock(id) : BLOCKS[id].solid,
    );
    for (const hit of hits) {
      if (wall && hit.distance > wall.dist + 0.05) continue;
      let o = hit.object;
      while (o && !o.userData.animal) o = o.parent;
      const a = o?.userData.animal;
      if (a && a.obj.position.distanceTo(this.E.player.pos) < 4.2) return a;
    }
    return null;
  }
  hit(a) {
    const E = this.E,
      it = selectedItem(E.data),
      spec = TYPES[a.kind];
    if (
      E.state !== "play" ||
      E.game.paused ||
      !a.obj.visible ||
      a.obj.position.distanceTo(E.player.pos) > 4.2 ||
      a.flash > 0
    )
      return false;
    if (
      Object.entries(spec.drops).some(
        ([id, n]) => amount(E.data, id) + n > STACK_MAX,
      )
    ) {
      E.game.ui.toast("A stack is full. Use some supplies first.", "blue");
      return false;
    }
    a.hp -= it?.tool === "sword" ? it.power : 1;
    a.flash = 0.25;
    audio.play("hit");
    E.player.facing = Math.atan2(
      a.obj.position.x - E.player.pos.x,
      a.obj.position.z - E.player.pos.z,
    );
    E.player.swing();
    E.fx.particles.dust(
      a.obj.position.clone().add(new THREE.Vector3(0, 0.5, 0)),
      5,
      "#ffeac7",
    );
    if (a.hp <= 0) {
      E.data.hunted[a.id] = E.data.worldTime + 150;
      a.obj.visible = false;
      for (const [id, n] of Object.entries(spec.drops)) {
        addItem(E.data, id, n);
        E.data.gathered[id] = (E.data.gathered[id] || 0) + n;
        E.lootVisual(id, n, a.obj.position);
      }
      E.game.ui.toast(
        Object.entries(spec.drops)
          .map(([id, n]) => `+${n} ${id}`)
          .join(" · "),
        "green",
      );
      E.game.save.persist();
      E.refreshHUD();
    }
    return true;
  }
  update(dt) {
    const E = this.E;
    for (const a of this.animals.values()) {
      const asleep = (E.data.hunted[a.id] || 0) > E.data.worldTime;
      a.obj.visible = !asleep;
      if (asleep) continue;
      if (E.data.hunted[a.id]) {
        delete E.data.hunted[a.id];
        a.hp = TYPES[a.kind].hp;
      }
      a.time += dt;
      a.flash = Math.max(0, a.flash - dt);
      a.model.mats.body.emissive.set(a.flash > 0 ? "#ad7730" : "#000000");
      const active = a.obj.position.distanceTo(E.player.pos) < 40;
      if (!active) continue;
      const walk = Math.sin(a.time * 0.65) > -0.1;
      if (a.obj.position.distanceTo(a.home) > 3.5)
        a.angle = Math.atan2(
          a.home.x - a.obj.position.x,
          a.home.z - a.obj.position.z,
        );
      else a.angle += Math.sin(a.time * 0.7) * dt * 0.4;
      if (walk && a.flash === 0) {
        const x = a.obj.position.x + Math.sin(a.angle) * dt * 0.65,
          z = a.obj.position.z + Math.cos(a.angle) * dt * 0.65;
        // Follow edited terrain instead of floating above a demolished floor.
        let y = Math.floor(a.obj.position.y) + 1;
        const floor = y - 4;
        while (y > floor && !E.world.solid(Math.floor(x), y - 1, Math.floor(z))) y--;

        if (
          Math.abs(y - a.obj.position.y) <= 1 &&
          !E.world.solid(Math.floor(x), y, Math.floor(z)) &&
          !E.world.solid(Math.floor(x), y + 1, Math.floor(z))
        )
          a.obj.position.set(x, y, z);
        else a.angle += dt * 4;
      }
      a.obj.rotation.y = a.angle;
      a.model.tail.rotation.y = Math.sin(a.time * 5) * 0.25;
      a.model.legs.forEach(
        (l, i) =>
          (l.rotation.x = walk
            ? Math.sin(a.time * 7 + (i % 2) * Math.PI) * 0.35
            : 0),
      );
    }
  }
  dispose() {
    for (const a of this.animals.values()) {
      this.E.group.remove(a.obj);
      disposeObj(a.obj);
    }
    this.animals.clear();
  }
}
